/**
 * Master API Service
 * マスタデータ関連のAPI通信を統合管理
 */

import config from '@/const'
import axios from 'axios'

export class MasterApiService {
  /**
   * 認証トークン取得
   */
  static getAuthToken() {
    try {
      // Vuexストアから取得を試行
      // if (
      //   window &&
      //   window.$nuxt &&
      //   window.$nuxt.$store &&
      //   window.$nuxt.$store.state.auth
      // ) {
      //   return window.$nuxt.$store.state.auth.token
      // }
      if (this.$store?.state?.auth?.token) {
        return this.$store.state.auth.token
      }

      // localStorage から取得を試行
      // const token = localStorage.getItem('auth-token')
      const token = localStorage.getItem('token')
      if (token) {
        return token
      }

      // デフォルトトークン（開発用）
      return 'test-token'
    } catch (error) {
      console.warn('トークン取得エラー、デフォルトトークンを使用:', error)
      return 'test-token'
    }
  }

  /**
   * URLSearchParamsへの変換（元のsetParams_S相当）
   */
  static setParamsS(params) {
    const params_ = new URLSearchParams()
    if (params) {
      const keys = Object.keys(params)
      for (let i = 0; i < keys.length; i++) {
        if (keys[i] === 'p') {
          params_.append(keys[i], params[keys[i]])
        } else if (
          params[keys[i]] !== null &&
          typeof params[keys[i]] === 'object'
        ) {
          params_.append(
            'sprm[' + keys[i] + ']',
            JSON.stringify(params[keys[i]])
          )
        } else {
          params_.append('sprm[' + keys[i] + ']', params[keys[i]])
        }
      }
    }
    return params_
  }

  /**
   * basicPosts_S相当のPOSTリクエスト（シンプル版）
   */
  static async basicPostsS(url, data = {}) {
    // 認証トークンの取得
    const token = this.getAuthToken()

    const headers = {
      [config.BASE_API_HEADER_KEY]: token
    }

    const response = await axios.post(url, this.setParamsS(data), {
      headers,
      withCredentials: true
    })
    return response
  }

  /**
   * basicPosts相当のPOSTリクエスト（認証付き）
   */
  static async basicPosts(url, data = {}, withAuth = false) {
    const headers = {}

    if (withAuth) {
      const token = this.getAuthToken()
      headers[config.BASE_API_HEADER_KEY] = token
    }

    const response = await axios.post(url, data, {
      headers,
      withCredentials: true
    })
    return response
  }

  /**
   * マスタ一覧取得
   * @param {string|null} masterType - マスタタイプ（null時は全項目取得）
   * @param {string|null} year - 年度フィルター
   */
  static async getMasterList(masterType = null, year = null) {
    try {
      let url = `${config.BASE_API_URL}/?s=api&c=api&m=masters/lists`

      if (masterType) url += `&type=${masterType}`
      if (year) {
        const yearParam = year
          .toString()
          .replace(/年度$/, '')
          .replace(/[^\d]/g, '')
        url += `&year=${yearParam}`
      }

      const response = await this.basicPostsS(url)

      if (!response?.data) {
        throw new Error('APIレスポンスが不正です')
      }

      return {
        success: response.data.status === 200,
        data: response.data.body || {},
        message: response.data.alert?.message || '',
        status: response.data.status
      }
    } catch (error) {
      console.error('マスタ一覧取得エラー:', error)
      return {
        success: false,
        data: {},
        message: `マスタデータの取得に失敗しました: ${error.message}`,
        status: 500
      }
    }
  }

  /**
   * 年度一覧取得
   */
  static async getYearsList() {
    try {
      const response = await this.basicPostsS(
        `${config.BASE_API_URL}/api/?s=api&c=api&m=years/lists`,
        {}
      )

      // PHP Noticeなどが含まれる場合のJSON抽出
      let responseData = response.data
      if (typeof responseData === 'string') {
        const jsonStartIndex = responseData.indexOf('{')
        if (jsonStartIndex !== -1) {
          responseData = JSON.parse(responseData.substring(jsonStartIndex))
        }
      }

      if (responseData?.status === 200 && responseData.body?.years) {
        return {
          success: true,
          years: responseData.body.years.sort((a, b) => b - a),
          currentYear: responseData.body.current_year
        }
      }

      return {
        success: false,
        years: [],
        message: responseData.alert?.message || '年度データの取得に失敗しました'
      }
    } catch (error) {
      console.error('年度データ取得エラー:', error)
      return {
        success: false,
        years: [],
        message: '年度データの取得に失敗しました'
      }
    }
  }

  /**
   * マスタタイプ一覧取得
   */
  static async getMasterTypesList() {
    try {
      const response = await this.basicPostsS(
        `${config.BASE_API_URL}/?s=api&c=api&m=master_types/lists`,
        {}
      )

      let responseData = response.data
      if (typeof responseData === 'string') {
        responseData = responseData.trim()
        let jsonStartIndex = responseData.indexOf('{')
        let parsed = false

        while (jsonStartIndex !== -1 && !parsed) {
          try {
            responseData = JSON.parse(responseData.substring(jsonStartIndex))
            parsed = true
          } catch (e) {
            responseData = responseData.substring(0, responseData.length - 1)
          }
        }

        if (!parsed) {
          throw new Error('API応答の解析に失敗しました。')
        }
      }

      if (responseData?.status === 200 && responseData.body?.master_types) {
        const masterTypes = responseData.body.master_types.map(item => ({
          value: item.type,
          label: item.name
        }))

        return {
          success: true,
          masterTypes
        }
      }

      throw new Error(
        responseData.alert?.message || 'マスタ種別一覧の取得に失敗しました。'
      )
    } catch (error) {
      console.error('マスタタイプ取得エラー:', error)
      return {
        success: false,
        masterTypes: [],
        message: error.message || 'API呼び出し中にエラーが発生しました。'
      }
    }
  }

  /**
   * マスタ新規作成
   * @param {string} masterType - マスタタイプ
   * @param {Object} data - 作成データ
   */
  static async createMasterItem(masterType, data) {
    try {
      const url = `${config.BASE_API_URL}/?s=api&c=api&m=masters/create&type=${masterType}`

      const response = await this.basicPostsS(url, data)

      if (!response?.data) {
        throw new Error('APIレスポンスが不正です')
      }

      return {
        success: response.data.status === 201,
        data: response.data.body || {},
        message: response.data.alert?.message || '',
        status: response.data.status
      }
    } catch (error) {
      console.error('マスタ作成エラー:', error)
      return {
        success: false,
        data: {},
        message: `マスタデータの作成に失敗しました: ${error.message}`,
        status: 500
      }
    }
  }

  /**
   * マスタ更新
   * @param {string} masterType - マスタタイプ
   * @param {string|number} id - マスタID
   * @param {Object} data - 更新データ
   */
  static async updateMasterItem(masterType, id, data) {
    try {
      const url = `${config.BASE_API_URL}/?s=api&c=api&m=masters/update&type=${masterType}&id=${id}`

      const response = await this.basicPostsS(url, data)

      if (!response?.data) {
        throw new Error('APIレスポンスが不正です')
      }

      return {
        success: response.data.status === 200,
        data: response.data.body || {},
        message: response.data.alert?.message || '',
        status: response.data.status
      }
    } catch (error) {
      console.error('マスタ更新エラー:', error)
      return {
        success: false,
        data: {},
        message: `マスタデータの更新に失敗しました: ${error.message}`,
        status: 500
      }
    }
  }

  /**
   * マスタ削除
   * @param {string} masterType - マスタタイプ
   * @param {string|number} id - マスタID
   */
  static async deleteMasterItem(masterType, id) {
    try {
      const url = `${config.BASE_API_URL}/?s=api&c=api&m=masters/delete&type=${masterType}&id=${id}`

      const response = await this.basicPostsS(url, {})

      if (!response?.data) {
        throw new Error('APIレスポンスが不正です')
      }

      return {
        success: response.data.status === 200,
        data: response.data.body || {},
        message: response.data.alert?.message || '',
        status: response.data.status
      }
    } catch (error) {
      console.error('マスタ削除エラー:', error)
      return {
        success: false,
        data: {},
        message: `マスタデータの削除に失敗しました: ${error.message}`,
        status: 500
      }
    }
  }

  /**
   * マスタ詳細取得
   * @param {string} masterType - マスタタイプ
   * @param {string|number} id - マスタID
   */
  static async getMasterItem(masterType, id) {
    try {
      const url = `${config.BASE_API_URL}/?s=api&c=api&m=masters/detail&type=${masterType}&id=${id}`

      const response = await this.basicPostsS(url, {})

      if (!response?.data) {
        throw new Error('APIレスポンスが不正です')
      }

      return {
        success: response.data.status === 200,
        data: response.data.body || {},
        message: response.data.alert?.message || '',
        status: response.data.status
      }
    } catch (error) {
      console.error('マスタ詳細取得エラー:', error)
      return {
        success: false,
        data: {},
        message: `マスタデータの取得に失敗しました: ${error.message}`,
        status: 500
      }
    }
  }

  /**
   * CSVインポート
   */
  static async importCsv(masterType, csvData, filename) {
    try {
      const params = {
        master_type: masterType,
        csv_data: csvData,
        filename: filename
      }

      const response = await this.basicPosts(
        `${config.BASE_API_URL}/master/import`,
        params,
        true // 認証ヘッダー送信
      )

      if (response.data?.status === 200) {
        return {
          success: true,
          message:
            response.data.alert?.message || 'CSVインポートが完了しました',
          importedCount: response.data.body?.imported_count
        }
      }

      return {
        success: false,
        message: response.data?.alert?.message || 'CSVインポートに失敗しました'
      }
    } catch (error) {
      console.error('CSVインポートエラー:', error)
      return {
        success: false,
        message: 'CSVインポートに失敗しました'
      }
    }
  }
}
